/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { BaseFeatureMock } from '../mockServer/featureMock';
import {
    CashBoxTaskItemDto,
    CashBoxTasksListDto,
    CashBoxTaskStatus,
    CashBoxTaskType,
    CreateCashBoxTaskRequestParams,
    CreateCashBoxTaskResponseDto,
    RoughCashboxesListDto,
    RoughCashboxesListRequestParams,
    SuitableTargetVersionsDto,
    DeviceServiceListItem,
    DevicePlatform,
    ServiceOperationBody,
    UploadInitializationFileBody,
} from './serviceClient.typings';
import {
    cashBoxCertUpdateTaskItemFactory,
    cashBoxChecksumTaskItemFactory,
    cashBoxEnvInfoTaskItemFactory,
    cashBoxFwUpdateTaskItemFactory,
    cashBoxLogsTaskItemFactory,
    createCashboxTaskResponseFactory,
    roughCashboxesListItemFactory,
    deviceDtoFactory,
    cashBoxDumpTaskItemFactory,
    cashBoxRepairTaskItemFactory,
} from './serviceFactories';

const GET_ROUGH_CASHBOXES_URL: RegExp = /\/service\/cashboxes$/;
const CREATE_CASHBOX_TASK = /\/service\/tasks$/;
const CREATE_CASHBOXES_TASKS = /\/service\/tasks\/multi$/;
const GET_TASK_HISTORY = /\/service\/tasks\/history$/;
const GET_TASKS_STATUS = /\/service\/tasks\/status$/;
const SUITABLE_TARGET_VERSIONS = /\/service\/firmwares\/\w+/;
const SERVICE_UPLOAD_FILE = /\/service\/uploadInitFile/;
const SEARCH_FACTORY_NUMBERS = /\/service\/factoryNumbers/;
const DEVICES_LIST = /\/service\/devices/;
const DEVICE_OPERATION = /\/service\/deviceOperation/;
const DOWNLOAD_CASHBOX_LOGS = /\/service\/logs\/\w+/;

class ServiceFeatureMock extends BaseFeatureMock {
    public readonly roughCashboxes = this.route<RoughCashboxesListDto, RoughCashboxesListRequestParams>({
        method: 'post',
        url: GET_ROUGH_CASHBOXES_URL,
        defaultResponse: (config) => {
            const body: RoughCashboxesListRequestParams = JSON.parse(config.data);
            const data: RoughCashboxesListDto = {
                afterKeyTaxNumber: '234318932',
                cashBoxes: roughCashboxesListItemFactory.list({ count: body.limit }),
            };
            roughCashboxesListItemFactory.resetCount();
            return [200, data];
        },
        delayMs: 100,
    });

    public readonly createCashboxTask = this.route<CreateCashBoxTaskResponseDto, CreateCashBoxTaskRequestParams>({
        method: 'post',
        url: CREATE_CASHBOX_TASK,
        defaultResponse: () => [200, createCashboxTaskResponseFactory.item()],
        delayMs: () => Math.random() * 3000,
    });

    public readonly createCashboxesTasks = this.route<CreateCashBoxTaskResponseDto[], CreateCashBoxTaskRequestParams[]>(
        {
            method: 'post',
            url: CREATE_CASHBOXES_TASKS,
            defaultResponse: (config) => {
                const body: CreateCashBoxTaskRequestParams[] = JSON.parse(config.data);
                const data = createCashboxTaskResponseFactory.list({ count: body.length });
                createCashboxTaskResponseFactory.resetCount();
                return [200, data];
            },
            delayMs: () => Math.random() * 3000,
        },
    );

    public readonly taskHistory = this.route<CashBoxTasksListDto>({
        method: 'get',
        url: GET_TASK_HISTORY,
        defaultResponse: (config) => {
            let items: CashBoxTaskItemDto[] = [];
            switch (config.params.name) {
                case CashBoxTaskType.CERTUPDATE:
                    items = cashBoxCertUpdateTaskItemFactory.list({ count: config.params.limit });
                    if (items[0].id === '10001') items[0].status = CashBoxTaskStatus.CREATED;
                    break;
                case CashBoxTaskType.FWUPDATE:
                    items = cashBoxFwUpdateTaskItemFactory.list({ count: config.params.limit });
                    break;
                case CashBoxTaskType.LOGS:
                    items = cashBoxLogsTaskItemFactory.list({ count: config.params.limit });
                    break;
                case CashBoxTaskType.ENVINFO:
                    items = cashBoxEnvInfoTaskItemFactory.list({ count: config.params.limit });
                    break;
                case CashBoxTaskType.CHECKSUM:
                    items = cashBoxChecksumTaskItemFactory.list({ count: config.params.limit });
                    break;
            }
            return [200, { count: config.params.limit * 3, items }];
        },
        delayMs: () => Math.random() * 300,
    });

    public readonly taskStatuses = this.route<CashBoxTasksListDto>({
        method: 'get',
        url: GET_TASKS_STATUS,
        defaultResponse: (config) => {
            let items: CashBoxTaskItemDto[] = [];
            const count = config.params.cashBoxTaxNumbers.length;
            switch (config.params.name) {
                case CashBoxTaskType.CERTUPDATE:
                    items = cashBoxCertUpdateTaskItemFactory.list({ count });
                    cashBoxCertUpdateTaskItemFactory.resetCount();
                    break;
                case CashBoxTaskType.FWUPDATE:
                    items = cashBoxFwUpdateTaskItemFactory.list({ count });
                    cashBoxFwUpdateTaskItemFactory.resetCount();
                    break;
                case CashBoxTaskType.LOGS:
                    items = cashBoxLogsTaskItemFactory.list({ count });
                    cashBoxLogsTaskItemFactory.resetCount();
                    break;
                case CashBoxTaskType.ENVINFO:
                    items = cashBoxEnvInfoTaskItemFactory.list({ count });
                    cashBoxEnvInfoTaskItemFactory.resetCount();
                    break;
                case CashBoxTaskType.CHECKSUM:
                    items = cashBoxChecksumTaskItemFactory.list({ count });
                    cashBoxChecksumTaskItemFactory.resetCount();
                    break;
                case CashBoxTaskType.DUMP:
                    items = cashBoxDumpTaskItemFactory.list({ count });
                    cashBoxDumpTaskItemFactory.resetCount();
                    break;
                case CashBoxTaskType.REPAIR:
                    items = cashBoxRepairTaskItemFactory.list({ count });
                    cashBoxRepairTaskItemFactory.resetCount();
                    break;
            }
            items.forEach((item, index) => (item.cashBoxTaxNumber = config.params.cashBoxTaxNumbers[index]));
            return [200, { count, items }];
        },
        delayMs: () => Math.random() * 300,
    });

    public readonly suitableTargetVersions = this.route<SuitableTargetVersionsDto>({
        method: 'get',
        url: SUITABLE_TARGET_VERSIONS,
        defaultResponse: (config) => {
            const platformType = config.url.split('/').pop();
            const data: SuitableTargetVersionsDto = {
                versions: [
                    '3.31.0.stm',
                    '2.23.0.stm',
                    '2.25.0.stm',
                    '2.26.0.stm',
                    '2.29.1.stm',
                    '4.28.0',
                    '2.27.0',
                    '2.30.0.stm',
                    '2.22.1.stm',
                    '2.24.0.stm',
                    '4.27.1.stm',
                ],
            };
            if (platformType === DevicePlatform.LPC)
                data.versions = [
                    '4.22.1.lpc',
                    '2.23.0.lpc',
                    '3.25.0.lpc',
                    '4.28.0',
                    '2.27.0',
                    '4.26.0.lpc',
                    '3.29.1.lpc',
                    '2.28.0.lpc',
                ];

            return [200, data];
        },
        delayMs: 3000,
    });

    public readonly uploadFile = this.route<{}, UploadInitializationFileBody>({
        method: 'post',
        url: SERVICE_UPLOAD_FILE,
        defaultResponse: () => [200, {}],
        delayMs: 1000,
    });

    public readonly searchFactoryNumbers = this.route<string[]>({
        method: 'get',
        url: SEARCH_FACTORY_NUMBERS,
        defaultResponse: (config) => {
            const { factoryNumber, limit } = config.params;
            const arr = Array(limit).fill(0);
            return [200, arr.map(() => factoryNumber + (Math.random() * 100000).toFixed())];
        },
        delayMs: 500,
    });

    public readonly devicesList = this.route<DeviceServiceListItem[], { factoryNumbers: string[] }>({
        method: 'post',
        url: DEVICES_LIST,
        defaultResponse: (config) => {
            const body: { factoryNumbers: string[] } = JSON.parse(config.data);
            const data = deviceDtoFactory(body.factoryNumbers).list({ count: body.factoryNumbers.length });
            return [200, data];
        },
        delayMs: 500,
    });

    public readonly deviceOperation = this.route<{}, ServiceOperationBody>({
        method: 'post',
        url: DEVICE_OPERATION,
        defaultResponse: () => [200, {}],
        delayMs: 500,
    });

    public readonly downloadCashBoxLogs = this.route<ArrayBuffer>({
        method: 'get',
        url: DOWNLOAD_CASHBOX_LOGS,
        defaultResponse: () => new Uint8Array([0x50, 0x4b, 0x03, 0x04]).buffer,
        delayMs: 300,
    });

    protected onReset(): void {
        roughCashboxesListItemFactory.resetCount();
        createCashboxTaskResponseFactory.resetCount();
        cashBoxCertUpdateTaskItemFactory.resetCount();
        cashBoxFwUpdateTaskItemFactory.resetCount();
        cashBoxLogsTaskItemFactory.resetCount();
        cashBoxEnvInfoTaskItemFactory.resetCount();
        cashBoxChecksumTaskItemFactory.resetCount();
        cashBoxDumpTaskItemFactory.resetCount();
        cashBoxRepairTaskItemFactory.resetCount();
    }
}

export const serviceMock = new ServiceFeatureMock();

/** Backward-compat shim - dev mock mode (mockAdapter.ts) keeps calling this untouched. */
export function addServiceMockApi(mockAdapter: MockAdapter): void {
    serviceMock.register(mockAdapter, { delay: true });
}
